/* =============================================================================
   RUVIA — shared behaviour (Shopify theme)

   Ported from the static site. The cart is no longer a localStorage array —
   it is Shopify's real cart, driven through the Cart AJAX API. The drawer UI,
   focus trap, toast, reveal and dispatch clock are unchanged in behaviour.

   Exposes window.Ruvia for product.js.
   ============================================================================= */
(function () {
  'use strict';

  const T = window.RuviaTheme || {};
  const ROUTES = T.routes || {};

  /* ---------------------------------------------------------------- storage */
  // Private modes can throw on access — never let that break a page.
  const store = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
    }
  };

  /* ---------------------------------------------------------------- currency */
  // Shopify returns money in the smallest currency unit and Markets has already
  // converted it, so this only has to format — no rate table, no disclaimer.
  const CURRENCY = T.currency || 'USD';

  function money(cents) {
    const amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency: CURRENCY
      }).format(amount);
    } catch {
      return '$' + amount.toFixed(2);
    }
  }

  /* -------------------------------------------------------------------- cart */
  let cart = { items: [], item_count: 0, total_price: 0 };
  const cartListeners = [];

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(res => res.json().then(data => {
      if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
      return data;
    }));
  }

  function fetchCart() {
    return fetch(ROUTES.cart, { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(applyCart);
  }

  function applyCart(data) {
    cart = data;
    cartListeners.forEach(fn => fn(cart));
    renderCart();
    renderCount();
    return cart;
  }

  // line: { id: variantId, quantity, selling_plan?, properties? }
  function addToCart(line) {
    return request(ROUTES.cart_add, { items: [line] })
      .then(fetchCart)
      .catch(err => { toast(err.message || T.strings.cartError); throw err; });
  }

  function setQty(key, qty) {
    return request(ROUTES.cart_change, { id: key, quantity: qty })
      .then(applyCart)
      .catch(err => { toast(err.message || T.strings.cartError); });
  }

  /* ------------------------------------------------------------------ toast */
  let toastEl, toastTimer;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      toastEl.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="m5 12 5 5L20 7"/></svg><span></span>';
      document.body.appendChild(toastEl);
    }
    toastEl.lastElementChild.textContent = message;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 2600);
  }

  /* ------------------------------------------------------------- focus trap */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function makePanel(panel, onClose) {
    let lastFocus = null;

    function onKey(e) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    return {
      open() {
        lastFocus = document.activeElement;
        document.addEventListener('keydown', onKey);
        document.body.classList.add('is-locked');
        const target = panel.querySelector(FOCUSABLE);
        if (target) setTimeout(() => target.focus(), 60);
      },
      close() {
        document.removeEventListener('keydown', onKey);
        document.body.classList.remove('is-locked');
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
    };
  }

  /* ==========================================================================
     DOM WIRING
     ========================================================================== */
  const $  = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => [...(root || document).querySelectorAll(sel)];
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------------------------------------------------------- sticky header */
  (function stickyHeader() {
    const header = $('.header');
    if (!header) return;
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px';
    document.body.prepend(sentinel);
    new IntersectionObserver(
      ([entry]) => header.classList.toggle('is-stuck', !entry.isIntersecting),
      { rootMargin: '-8px 0px 0px 0px' }
    ).observe(sentinel);
  })();

  /* -------------------------------------------------------------- mobile nav */
  (function mobileNav() {
    const nav = $('#nav');
    const burger = $('#burger');
    const scrim = $('#scrim');
    if (!nav || !burger) return;

    const panel = makePanel(nav, close);

    function open() {
      nav.classList.add('is-on');
      if (scrim) scrim.classList.add('is-on');
      burger.setAttribute('aria-expanded', 'true');
      panel.open();
    }
    function close() {
      if (!nav.classList.contains('is-on')) return;
      nav.classList.remove('is-on');
      const cartOpen = $('#drawer') && $('#drawer').classList.contains('is-on');
      if (scrim && !cartOpen) scrim.classList.remove('is-on');
      burger.setAttribute('aria-expanded', 'false');
      panel.close();
    }

    burger.addEventListener('click', () =>
      nav.classList.contains('is-on') ? close() : open());
    nav.addEventListener('click', e => { if (e.target.closest('a')) close(); });
    if (scrim) scrim.addEventListener('click', close);
  })();

  /* ------------------------------------------------------------ cart drawer */
  const drawer = $('#drawer');
  const scrim = $('#scrim');
  const drawerPanel = drawer ? makePanel(drawer, closeCart) : null;

  function openCart() {
    if (!drawer) return;
    drawer.classList.add('is-on');
    scrim.classList.add('is-on');
    $('#cartBtn').setAttribute('aria-expanded', 'true');
    drawerPanel.open();
  }
  function closeCart() {
    if (!drawer || !drawer.classList.contains('is-on')) return;
    drawer.classList.remove('is-on');
    const navOpen = $('#nav') && $('#nav').classList.contains('is-on');
    if (!navOpen) scrim.classList.remove('is-on');
    $('#cartBtn').setAttribute('aria-expanded', 'false');
    drawerPanel.close();
  }

  function renderCount() {
    const badge = $('#cartCount');
    if (!badge) return;
    const n = cart.item_count || 0;
    badge.textContent = n;
    badge.classList.toggle('is-on', n > 0);
    if (n > 0) {
      badge.classList.remove('is-bump');
      void badge.offsetWidth;          // restart the animation
      badge.classList.add('is-bump');
    }
    const btn = $('#cartBtn');
    if (btn) btn.setAttribute('aria-label', n === 1 ? 'Cart, 1 item' : `Cart, ${n} items`);
  }

  function renderCart() {
    const body = $('#cartBody');
    if (!body) return;

    if (!cart.items || !cart.items.length) {
      body.innerHTML =
        '<div class="cart-empty">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
        '<path d="M3 3h2l2.4 12.4A2 2 0 0 0 9.4 17h8.2a2 2 0 0 0 2-1.6L21 8H6"/>' +
        '<circle cx="10" cy="20.5" r="1.2"/><circle cx="18" cy="20.5" r="1.2"/></svg>' +
        '<p>Your cart is empty.</p>' +
        '<a class="btn btn--ghost btn--sm" href="' + (T.routes.shop || T.routes.all_products || '/collections/all') + '">Browse the formula</a>' +
        '</div>';
    } else {
      // Free-shipping threshold is configured in theme settings, in store currency.
      const threshold = (T.freeShipOver || 0) * 100;
      const total = cart.total_price || 0;
      const left = threshold - total;
      const meterPct = threshold ? Math.min(100, (total / threshold) * 100) : 100;

      const meter = threshold
        ? '<div class="ship-meter">' +
          '<p>' + (left > 0
            ? 'Add <b>' + money(left) + '</b> for free shipping'
            : '<b>Free shipping unlocked.</b>') + '</p>' +
          '<div class="ship-meter__bar"><i style="width:' + meterPct + '%"></i></div>' +
          '</div>'
        : '';

      body.innerHTML = meter + cart.items.map(line => {
        // Shopify sizes a CDN image by inserting _128x *before* the extension —
        // dropping the extension instead yields a 404 and an empty thumbnail.
        const img = line.image
          ? '<img class="line__thumb" src="' +
            esc(line.image.replace(/(\.(?:png|jpe?g|gif|webp|avif))(\?|$)/i, '_128x$1$2')) +
            '" width="64" height="84" alt="" loading="lazy">'
          : '<span class="line__thumb"></span>';
        // Subscription lines carry a selling plan name worth showing.
        const meta = [line.variant_title, line.selling_plan_allocation
          ? line.selling_plan_allocation.selling_plan.name : null]
          .filter(v => v && v !== 'Default Title').join(' · ');

        return '<div class="line">' + img +
          '<div>' +
          '<p class="line__name"><a href="' + esc(line.url) + '">' + esc(line.product_title) + '</a></p>' +
          (meta ? '<p class="line__meta">' + esc(meta) + '</p>' : '') +
          '<span class="qty">' +
          '<button type="button" data-qty="' + esc(line.key) + '" data-step="-1" aria-label="Decrease quantity">&minus;</button>' +
          '<span>' + line.quantity + '</span>' +
          '<button type="button" data-qty="' + esc(line.key) + '" data-step="1" aria-label="Increase quantity">+</button>' +
          '</span></div>' +
          '<div class="line__right">' +
          '<span class="line__price">' + money(line.final_line_price) + '</span>' +
          '<button type="button" class="line__remove" data-remove="' + esc(line.key) + '">Remove</button>' +
          '</div></div>';
      }).join('');
    }

    const foot = $('#cartFoot');
    if (foot) foot.hidden = !(cart.items && cart.items.length);
    const sub = $('#cartTotal');
    if (sub) sub.textContent = money(cart.total_price);
  }

  if (drawer) {
    $('#cartBtn').addEventListener('click', e => { e.preventDefault(); openCart(); });
    $('#cartClose').addEventListener('click', closeCart);
    scrim.addEventListener('click', closeCart);

    $('#cartBody').addEventListener('click', e => {
      const step = e.target.closest('[data-qty]');
      if (step) {
        const line = cart.items.find(l => l.key === step.dataset.qty);
        if (line) setQty(line.key, line.quantity + Number(step.dataset.step));
        return;
      }
      const remove = e.target.closest('[data-remove]');
      if (remove) setQty(remove.dataset.remove, 0);
    });
  }

  /* ------------------------------------------------- "add to cart" anywhere */
  // Any element with data-add="<variantId>" adds one unit. No section ships one
  // by default any more; kept so a custom_liquid or app block can still use it.
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-add]');
    if (!btn || !btn.dataset.add) return;
    e.preventDefault();
    btn.classList.add('is-busy');
    addToCart({ id: Number(btn.dataset.add), quantity: 1 })
      .then(() => {
        toast(btn.dataset.addLabel || T.strings.addedToCart || 'Added to cart');
        openCart();
      })
      .finally(() => btn.classList.remove('is-busy'));
  });

  /* ------------------------------------------------- honest dispatch clock */
  // States a real fact: how long until the 3pm ET same-day dispatch cutoff.
  (function dispatchClock() {
    const el = $('#dispatch');
    if (!el) return;

    function nyParts(date) {
      const f = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', hour12: false,
        weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(date);
      const get = t => f.find(p => p.type === t).value;
      return { day: get('weekday'), h: +get('hour') % 24, m: +get('minute'), s: +get('second') };
    }

    function tick() {
      const p = nyParts(new Date());
      const weekend = p.day === 'Sat' || p.day === 'Sun';
      const secsNow = p.h * 3600 + p.m * 60 + p.s;
      const cutoff = 15 * 3600;                       // 3:00pm ET

      if (weekend || secsNow >= cutoff) {
        el.textContent = weekend ? 'ships Monday' : 'ships next business day';
        return;
      }
      const left = cutoff - secsNow;
      const pad = n => String(n).padStart(2, '0');
      el.innerHTML = 'order within <span class="announce__time">' +
        pad(Math.floor(left / 3600)) + ':' + pad(Math.floor(left / 60) % 60) + ':' + pad(left % 60) +
        '</span> to ship today';
    }

    tick();
    setInterval(tick, 1000);
  })();

  /* -------------------------------------------------------- reveal on scroll */
  (function reveal() {
    const items = $$('[data-reveal]');
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach(el => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: .08 });
    items.forEach(el => io.observe(el));
  })();

  /* ------------------------------------------------------ animated rating bars */
  (function bars() {
    const groups = $$('.bars');
    if (!groups.length || !('IntersectionObserver' in window)) {
      $$('.bars .bar i').forEach(i => { i.style.width = i.dataset.w + '%'; });
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        $$('.bar i', entry.target).forEach(i => { i.style.width = i.dataset.w + '%'; });
        obs.unobserve(entry.target);
      });
    }, { threshold: .3 });
    groups.forEach(g => io.observe(g));
  })();

  /* --------------------------------------------------------------- newsletter */
  // The form now posts to Shopify (customer create). Client validation only, so
  // an obvious typo is caught before a round trip.
  $$('.signup').forEach(form => {
    const msg = form.querySelector('.form-msg') || form.parentElement.querySelector('.form-msg');
    form.addEventListener('submit', e => {
      const input = form.querySelector('input[type="email"]');
      const value = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(value)) {
        e.preventDefault();
        if (msg) { msg.dataset.state = 'err'; msg.textContent = 'Please enter a valid email address.'; }
        input.focus();
      }
    });
  });

  /* ------------------------------------------------------- localization form */
  // Submitting reloads with the chosen country so Shopify recalculates prices.
  $$('.localization-form select').forEach(select => {
    select.addEventListener('change', () => select.form.submit());
  });

  /* ---------------------------------------------------------------- year stamp */
  $$('[data-year]').forEach(el => { el.textContent = new Date().getFullYear(); });

  /* --------------------------------------------- policy table of contents */
  // Marks the section currently under the sticky header. Rather than tracking
  // intersection ratios — which misbehave on sections taller than the viewport
  // and on the last one, too short to ever win — this keeps every heading's
  // position and picks the last one above the scroll line.
  (function policyToc() {
    const nav = $('.policy__nav-list');
    if (!nav) return;

    const links = $$('a[href^="#"]', nav);
    const entries = links
      .map(a => ({ link: a, target: document.getElementById(decodeURIComponent(a.hash.slice(1))) }))
      .filter(e => e.target);
    if (!entries.length) return;

    // Clicking a link parks its section scroll-padding-top (html) plus
    // scroll-margin-top (the article) below the viewport top. The detection
    // line has to sit just past that, or the click lands and the *previous*
    // section stays marked. Read both back rather than duplicating the numbers.
    // Geometry is measured up front so scrolling costs arithmetic instead of a
    // layout read per section — a 29-part document would otherwise thrash.
    let tops = [];
    let offset = 0;
    let maxScroll = 0;
    function measure() {
      // Clicking a link parks its section scroll-padding-top (html) plus
      // scroll-margin-top (the article) below the viewport top. The detection
      // line sits just past that, or a click lands and the *previous* section
      // stays marked. Read both back rather than duplicating the numbers.
      const pad = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const margin = parseFloat(getComputedStyle(entries[0].target).scrollMarginTop) || 0;
      offset = pad + margin + 8;
      tops = entries.map(e => e.target.getBoundingClientRect().top + window.scrollY);
      maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    }

    let current = null;
    function update() {
      const line = window.scrollY + offset;
      let index = 0;
      for (let i = 0; i < tops.length; i++) {
        if (tops[i] <= line) index = i;
        else break;
      }
      // At the very bottom the last section is the one being read, even when
      // it is too short for its heading to ever cross the line.
      if (window.scrollY >= maxScroll - 2) index = entries.length - 1;

      const active = entries[index];
      if (active === current) return;
      if (current) current.link.removeAttribute('aria-current');
      active.link.setAttribute('aria-current', 'true');
      current = active;
      // Keep the active chip visible in the horizontal rail on mobile.
      if (getComputedStyle(nav).overflowX === 'auto') {
        active.link.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    function remeasure() { measure(); update(); }

    addEventListener('scroll', update, { passive: true });
    addEventListener('resize', remeasure, { passive: true });
    // Late-loading images and webfonts move every heading below them.
    addEventListener('load', remeasure);
    remeasure();
  })();

  /* ----------------------------------------------------------------- boot */
  // Section rendering in the theme editor re-injects markup; re-render then.
  document.addEventListener('shopify:section:load', () => { renderCart(); renderCount(); });

  fetchCart();

  window.Ruvia = {
    money, addToCart, setQty, openCart, closeCart, toast, store, fetchCart,
    get cart() { return cart; },
    onCartChange(fn) { cartListeners.push(fn); }
  };
})();
